// RaisonDEtre.jsx
import React, { useState } from "react";
import {
    Box,
    Button,
    Divider,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";

const SUIT_SYMBOLS = {
    S: "♠",
    H: "♥",
    C: "♣",
    D: "♦",
};

const SUIT_NAMES = {
    es: {
        S: "Picas",
        H: "Corazones",
        C: "Tréboles",
        D: "Diamantes",
    },
    en: {
        S: "Spades",
        H: "Hearts",
        C: "Clubs",
        D: "Diamonds",
    },
    fr: {
        S: "Piques",
        H: "Cœurs",
        C: "Trèfles",
        D: "Carreaux",
    },
};

const INITIAL_REVEALS = {
    spot: false,
    black: false,
    suit: "",
    suitPicker: false,
    cards: false,
};

// Deck order from top to bottom.
const DECK = `
AS 4H 7C 10D KS 3S 6C 9D QS 2C 5C 8D JS
AH 4C 7D 10S KH 3H 6D 9S QH 2D 5D 8S JH
AC 4D 7S 10H KC 3C 6S 9H QC 2S 5S 8H JC
AD 4S 7H 10C KD 3D 6H 9C QD 2H 5H 8C JD
`
    .trim()
    .split(/\s+/)
    .map((code) => ({
        code,
        rank: code.slice(0, -1),
        suit: code.at(-1),
    }));

const TEXTS = {
    es: {
        subtitle:
            "Herramienta para practicar el juego Raison D'Etre de Henk Raison.",
        reset: "Resetear y cortar",
        spectator: "Espectador",
        hiddenCard: "Carta oculta",
        questions: "Preguntas",
        instructions:
            "Puedes pulsarlas en cualquier orden. Cada pregunta se desactiva después de utilizarla.",
        spotQuestion: "¿Quién tiene una carta de puntos?",
        blackQuestion: "¿Quién tiene una carta negra?",
        revealCards: "Mostrar cartas",
        spotLabel: "Carta de puntos",
        blackLabel: "Carta negra",
        yes: "Sí",
        no: "No",
        suitQuestion: "Preguntar por un palo",
        suitLabel: "Palo",
    },
    en: {
        subtitle: "Tool for practicing Henk Raison’s game Raison D’Être.",
        reset: "Reset and cut",
        spectator: "Spectator",
        hiddenCard: "Hidden card",
        questions: "Questions",
        instructions:
            "You can use them in any order. Each question is disabled after being used.",
        spotQuestion: "Who has a spot card?",
        blackQuestion: "Who has a black card?",
        revealCards: "Show cards",
        spotLabel: "Spot card",
        blackLabel: "Black card",
        yes: "Yes",
        no: "No",
        suitQuestion: "Ask about a suit",
        suitLabel: "Suit",
    },
    fr: {
        subtitle: "Outil pour s’entraîner au jeu Raison D’Être de Henk Raison.",
        reset: "Réinitialiser et couper",
        spectator: "Spectateur",
        hiddenCard: "Carte cachée",
        questions: "Questions",
        instructions:
            "Vous pouvez les utiliser dans n’importe quel ordre. Chaque question est désactivée après utilisation.",
        spotQuestion: "Qui a une carte à points ?",
        blackQuestion: "Qui a une carte noire ?",
        revealCards: "Montrer les cartes",
        spotLabel: "Carte à points",
        blackLabel: "Carte noire",
        yes: "Oui",
        no: "Non",
        suitQuestion: "Demander une couleur",
        suitLabel: "Couleur",
    },
};

const createRound = () => {
    const cut = Math.floor(Math.random() * DECK.length);

    return Array.from(
        { length: 4 },
        (_, index) => DECK[(cut + index) % DECK.length],
    );
};

const AnswerRow = ({ label, visible, answer, yes, no }) => (
    <Box
        sx={{
            minHeight: { xs: 34, sm: 34 },
            px: { xs: 0.3, sm: 1 },
            py: { xs: 0.25, sm: 0.65 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: { xs: "center", sm: "space-between" },
            gap: { xs: 0.1, sm: 1 },
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: visible ? "action.hover" : "transparent",
        }}
    >
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                fontSize: { xs: 8.5, sm: "0.75rem" },
                lineHeight: { xs: 1.05, sm: 1.66 },
                textAlign: "center",
            }}
        >
            {label}
        </Typography>

        <Typography
            variant="body2"
            sx={{
                minWidth: { xs: 0, sm: 38 },
                textAlign: { xs: "center", sm: "right" },
                fontSize: { xs: 10, sm: "0.875rem" },
                lineHeight: 1.1,
                fontWeight: 700,
                color: visible
                    ? answer
                        ? "success.main"
                        : "text.secondary"
                    : "text.disabled",
            }}
        >
            {visible ? (answer ? `✓ ${yes}` : no) : "—"}
        </Typography>
    </Box>
);

const PlayingCard = ({ card, hidden, hiddenLabel }) => {
    const isRed = card.suit === "H" || card.suit === "D";

    return (
        <Box
            role="img"
            aria-label={hidden ? hiddenLabel : card.code}
            sx={{
                position: "relative",
                width: 138,
                aspectRatio: "5 / 7",
                overflow: "hidden",
                border: 1,
                borderColor: hidden ? "error.dark" : "divider",
                borderRadius: 2,
                bgcolor: hidden ? "error.dark" : "background.paper",
                boxShadow: 3,
                userSelect: "none",
            }}
        >
            {hidden ? (
                <>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 7,
                            border: "2px solid",
                            borderColor: "common.white",
                            borderRadius: 1.25,
                            opacity: 0.9,
                            backgroundImage:
                                "linear-gradient(45deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 75%, rgba(255,255,255,.15) 75%), linear-gradient(45deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 75%, rgba(255,255,255,.15) 75%)",
                            backgroundPosition: "0 0, 10px 10px",
                            backgroundSize: "20px 20px",
                        }}
                    />

                    <Stack
                        sx={{
                            position: "absolute",
                            inset: 0,
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                px: { xs: 0.9, sm: 1.25 },
                                py: { xs: 0.35, sm: 0.5 },
                                bgcolor: "background.paper",
                                color: "text.primary",
                                fontWeight: 900,
                                fontSize: { xs: 17, sm: 20 },
                                lineHeight: 1,
                                letterSpacing: -1,
                            }}
                        >
                            R
                            <Box component="span" sx={{ color: "error.main" }}>
                                D’Ê
                            </Box>
                        </Typography>
                    </Stack>
                </>
            ) : (
                <>
                    <Stack
                        spacing={0}
                        sx={{
                            position: "absolute",
                            top: 9,
                            left: 10,
                            alignItems: "center",
                            color: isRed ? "error.main" : "text.primary",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: {
                                    xs: card.rank === "10" ? 17 : 19,
                                    sm: card.rank === "10" ? 20 : 23,
                                },
                                fontWeight: 800,
                                lineHeight: 1,
                            }}
                        >
                            {card.rank}
                        </Typography>
                        <Typography sx={{ fontSize: 21, lineHeight: 1 }}>
                            {SUIT_SYMBOLS[card.suit]}
                        </Typography>
                    </Stack>

                    <Typography
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: { xs: 46, sm: 54 },
                            color: isRed ? "error.main" : "text.primary",
                        }}
                    >
                        {SUIT_SYMBOLS[card.suit]}
                    </Typography>

                    <Stack
                        spacing={0}
                        sx={{
                            position: "absolute",
                            right: 10,
                            bottom: 9,
                            alignItems: "center",
                            color: isRed ? "error.main" : "text.primary",
                            transform: "rotate(180deg)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: {
                                    xs: card.rank === "10" ? 17 : 19,
                                    sm: card.rank === "10" ? 20 : 23,
                                },
                                fontWeight: 800,
                                lineHeight: 1,
                            }}
                        >
                            {card.rank}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: 18, sm: 21 },
                                lineHeight: 1,
                            }}
                        >
                            {SUIT_SYMBOLS[card.suit]}
                        </Typography>
                    </Stack>
                </>
            )}
        </Box>
    );
};

const RaisonDEtre = () => {
    const [language, setLanguage] = useState("es");
    const [cards, setCards] = useState(createRound);
    const [revealed, setRevealed] = useState(INITIAL_REVEALS);
    const text = TEXTS[language];

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Stack spacing={{ xs: 1, sm: 2.5 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: 1.25, sm: 2 }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontSize: { xs: 30, sm: 44 },
                                fontWeight: 950,
                                lineHeight: 1,
                                letterSpacing: -2,
                            }}
                        >
                            RAISON{" "}
                            <Box component="span" sx={{ color: "error.main" }}>
                                D’ÊTRE
                            </Box>
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: { xs: 0.5, sm: 1 },
                                fontSize: { xs: 12, sm: "0.875rem" },
                            }}
                        >
                            {text.subtitle}
                        </Typography>
                    </Box>

                    <Stack
                        direction={{ xs: "row", sm: "row" }}
                        spacing={1}
                        alignItems="stretch"
                    >
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            size="small"
                            value={language}
                            aria-label="Language"
                            onChange={(_, value) => value && setLanguage(value)}
                            sx={{
                                flex: { xs: 1, sm: "initial" },
                            }}
                        >
                            <ToggleButton value="es">ES</ToggleButton>
                            <ToggleButton value="en">EN</ToggleButton>
                            <ToggleButton value="fr">FR</ToggleButton>
                        </ToggleButtonGroup>

                        <Button
                            variant="contained"
                            onClick={() => {
                                setCards(createRound());
                                setRevealed({ ...INITIAL_REVEALS });
                            }}
                            sx={{
                                flex: { xs: 1, sm: "initial" },
                                px: { xs: 1, sm: 2 },
                                fontSize: { xs: 11, sm: "0.875rem" },
                            }}
                        >
                            {text.reset}
                        </Button>
                    </Stack>
                </Stack>

                <Divider />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "repeat(4, minmax(0, 1fr))",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: { xs: 0.5, sm: 2 },
                    }}
                >
                    {cards.map((card, index) => (
                        <Paper
                            key={`${index}-${card.code}`}
                            variant="outlined"
                            sx={{
                                p: { xs: 0.5, sm: 2 },
                                minWidth: 0,
                                borderRadius: { xs: 1, sm: 2 },
                            }}
                        >
                            <Typography
                                align="center"
                                variant="subtitle2"
                                sx={{
                                    mb: { xs: 0.5, sm: 1.5 },
                                    fontSize: { xs: 9.5, sm: "0.875rem" },
                                    lineHeight: 1.1,
                                    fontWeight: 700,
                                }}
                            >
                                <Box component="span">{text.spectator} </Box>

                                {index + 1}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <PlayingCard
                                    card={card}
                                    hidden={!revealed.cards}
                                    hiddenLabel={text.hiddenCard}
                                />
                            </Box>

                            <Stack
                                spacing={{ xs: 0.5, sm: 0.75 }}
                                sx={{ mt: { xs: 0.75, sm: 2 } }}
                            >
                                <AnswerRow
                                    label={text.spotLabel}
                                    visible={revealed.spot}
                                    answer={
                                        !["J", "Q", "K"].includes(card.rank)
                                    }
                                    yes={text.yes}
                                    no={text.no}
                                />

                                <AnswerRow
                                    label={text.blackLabel}
                                    visible={revealed.black}
                                    answer={["S", "C"].includes(card.suit)}
                                    yes={text.yes}
                                    no={text.no}
                                />

                                <AnswerRow
                                    label={
                                        revealed.suit
                                            ? `${text.suitLabel}: ${
                                                  SUIT_SYMBOLS[revealed.suit]
                                              } ${
                                                  SUIT_NAMES[language][
                                                      revealed.suit
                                                  ]
                                              }`
                                            : text.suitLabel
                                    }
                                    visible={Boolean(revealed.suit)}
                                    answer={card.suit === revealed.suit}
                                    yes={text.yes}
                                    no={text.no}
                                />
                            </Stack>
                        </Paper>
                    ))}
                </Box>

                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 1, sm: 2 },
                        borderRadius: 2,
                    }}
                >
                    <Stack spacing={{ xs: 0.75, sm: 1.5 }}>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: {
                                        xs: 14,
                                        sm: "1rem",
                                    },
                                    lineHeight: 1.2,
                                }}
                            >
                                {text.questions}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: {
                                        xs: "none",
                                        sm: "block",
                                    },
                                }}
                            >
                                {text.instructions}
                            </Typography>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{
                                display: {
                                    xs: "grid",
                                    sm: "flex",
                                },
                                gridTemplateColumns: {
                                    xs: "repeat(2, minmax(0, 1fr))",
                                },
                                gap: {
                                    xs: 0.75,
                                    sm: 1,
                                },
                                "& > button, & > div > button": {
                                    width: {
                                        xs: "100%",
                                        sm: "auto",
                                    },
                                    minWidth: 0,
                                    minHeight: {
                                        xs: 40,
                                        sm: 36,
                                    },
                                    px: {
                                        xs: 0.75,
                                        sm: 2,
                                    },
                                    py: {
                                        xs: 0.5,
                                        sm: 0.75,
                                    },
                                    fontSize: {
                                        xs: 10,
                                        sm: "0.875rem",
                                    },
                                    lineHeight: 1.1,
                                },
                            }}
                        >
                            <Button
                                variant="outlined"
                                disabled={revealed.spot}
                                onClick={() =>
                                    setRevealed((previous) => ({
                                        ...previous,
                                        spot: true,
                                    }))
                                }
                            >
                                {text.spotQuestion}
                            </Button>

                            <Button
                                variant="outlined"
                                disabled={revealed.black}
                                onClick={() =>
                                    setRevealed((previous) => ({
                                        ...previous,
                                        black: true,
                                    }))
                                }
                            >
                                {text.blackQuestion}
                            </Button>

                            <Box sx={{ minWidth: 0 }}>
                                <Button
                                    variant="outlined"
                                    disabled={Boolean(revealed.suit)}
                                    onClick={() =>
                                        setRevealed((previous) => ({
                                            ...previous,
                                            suitPicker: !previous.suitPicker,
                                        }))
                                    }
                                >
                                    {text.suitQuestion}
                                </Button>

                                {revealed.suitPicker && !revealed.suit && (
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        useFlexGap
                                        flexWrap="wrap"
                                        sx={{
                                            mt: {
                                                xs: 0.5,
                                                sm: 1,
                                            },
                                            display: {
                                                xs: "grid",
                                                sm: "flex",
                                            },
                                            gridTemplateColumns: {
                                                xs: "repeat(4, minmax(0, 1fr))",
                                            },
                                            gap: {
                                                xs: 0.4,
                                                sm: 0.75,
                                            },
                                        }}
                                    >
                                        {["S", "H", "C", "D"].map((suit) => (
                                            <Button
                                                key={suit}
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                    setRevealed((previous) => ({
                                                        ...previous,
                                                        suit,
                                                        suitPicker: false,
                                                    }))
                                                }
                                                sx={{
                                                    minWidth: {
                                                        xs: 0,
                                                        sm: 64,
                                                    },
                                                    px: {
                                                        xs: 0,
                                                        sm: 1,
                                                    },
                                                    py: {
                                                        xs: 0.25,
                                                        sm: 0.5,
                                                    },
                                                    color:
                                                        suit === "H" ||
                                                        suit === "D"
                                                            ? "error.main"
                                                            : "text.primary",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {SUIT_SYMBOLS[suit]}

                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: {
                                                            xs: "none",
                                                            sm: "inline",
                                                        },
                                                        ml: 0.5,
                                                    }}
                                                >
                                                    {SUIT_NAMES[language][suit]}
                                                </Box>
                                            </Button>
                                        ))}
                                    </Stack>
                                )}
                            </Box>

                            <Button
                                variant="contained"
                                disabled={revealed.cards}
                                onClick={() =>
                                    setRevealed((previous) => ({
                                        ...previous,
                                        cards: true,
                                    }))
                                }
                            >
                                {text.revealCards}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>
        </Box>
    );
};

export default RaisonDEtre;
